import { forwardRef, useCallback, useRef, useState } from 'react';
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
} from 'recharts';
import { useResizeObserver } from '../../hooks/useResizeObserver';
import { useMergeRefs } from '../../hooks/useMergeRefs';

export interface PieChartProps
  extends Omit<React.ComponentProps<typeof ResponsiveContainer>, 'children'> {
  background?: boolean;
  maxPercentage?: number;
  width?: number;
  valueText?: string;
  labelText?: string;
  data: { name: string; value: number; color: string }[];
  animationDuration?: number;
}

export const PieChart = forwardRef<HTMLDivElement, PieChartProps>(
  (
    {
      width: staticWidth,
      maxPercentage = 100,
      animationDuration,
      background = false,
      valueText,
      labelText,
      data,
      ...rest
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergeRefs<HTMLDivElement>(containerRef, ref);
    const [width, setWidth] = useState(staticWidth ?? 200);
    const [isMounted, setIsMounted] = useState(false);
    const radius = width / 2;

    const resizeCallback = useCallback((entry: ResizeObserverEntry) => {
      requestAnimationFrame(() => {
        if (!isMounted) {
          setIsMounted(true);
        }

        setWidth(entry.borderBoxSize[0].inlineSize);
      });
    }, []);

    useResizeObserver(containerRef, resizeCallback);

    const endAngle = 90 - 360 * (maxPercentage / 100);

    return (
      <ResponsiveContainer
        ref={mergedRef}
        width={staticWidth ?? '100%'}
        aspect={1}
        {...rest}
      >
        {isMounted || staticWidth ? (
          <RechartsPieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            {background && (
              <Pie
                data={[{ value: 100 }]}
                innerRadius={radius - 10}
                outerRadius={radius - 2}
                dataKey='value'
                startAngle={0}
                endAngle={-360}
                stroke='none'
                animationDuration={0}
              >
                <Cell fill={'#E8EAED'} />
              </Pie>
            )}
            <Pie
              data={data}
              innerRadius={radius - 12}
              outerRadius={radius}
              dataKey='value'
              startAngle={90}
              endAngle={endAngle}
              cornerRadius={10}
              stroke='white'
              strokeWidth={4}
              activeIndex={0}
              animationDuration={animationDuration}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            {valueText && (
              <text
                x={radius}
                y={radius - 15}
                textAnchor='middle'
                dominantBaseline='middle'
                className='label'
                fontSize={24}
                fontWeight='300'
                fontFamily='Inter'
              >
                {valueText}
              </text>
            )}
            {labelText && (
              <text
                x={radius}
                y={radius + 10}
                textAnchor='middle'
                dominantBaseline='middle'
                className='label'
                fontSize={14}
                fill='#888'
                fontFamily='Inter'
              >
                {labelText}
              </text>
            )}
          </RechartsPieChart>
        ) : (
          <></>
        )}
      </ResponsiveContainer>
    );
  },
);

PieChart.displayName = 'PieChart';
